import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileDown, Filter, Package, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Storico = () => {
  const [bins, setBins] = useState([]);
  const [filteredBins, setFilteredBins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [docciaFilter, setDocciaFilter] = useState('all');
  const [filterData, setFilterData] = useState('');
  const [raggruppaPerLotto, setRaggruppaPerLotto] = useState(false);

  useEffect(() => {
    fetchBins();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, docciaFilter, filterData, bins]);

  const fetchBins = async () => {
    try {
      const response = await axios.get(`${API}/bins`);
      const binsConGiacenza = response.data.filter(b => b.kg_attuali > 0);
      const sortedBins = binsConGiacenza.sort((a, b) => a.doccia_number - b.doccia_number);
      setBins(sortedBins);
    } catch (error) {
      toast.error('Errore nel caricamento disponibilita');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...bins];

    if (docciaFilter !== 'all') {
      filtered = filtered.filter(b => b.doccia_number === parseInt(docciaFilter));
    }

    if (filterData) {
      filtered = filtered.filter(b => {
        const binDate = format(new Date(b.data_inizio), 'yyyy-MM-dd');
        return binDate === filterData;
      });
    }

    if (searchTerm) {
      filtered = filtered.filter(b => 
        b.bin_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.lotto_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.fornitore.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredBins(filtered);
  };

  const handleExport = async (exportFormat) => {
    try {
      const params = new URLSearchParams();
      if (docciaFilter !== 'all') params.append('doccia', docciaFilter);
      if (filterData) params.append('data', filterData);
      if (searchTerm) params.append('search', searchTerm);
      if (raggruppaPerLotto) params.append('raggruppa_lotto', 'true');
      
      const apiUrl = `${API}/disponibilita/export/${exportFormat}${params.toString() ? '?' + params.toString() : ''}`;
      
      if (exportFormat === 'pdf') {
        // Per PDF: apri in nuova finestra per garantire download
        window.open(apiUrl, '_blank');
        toast.success('Report PDF in download...');
      } else {
        // Per Excel: usa blob download
        const response = await axios.get(apiUrl, { responseType: 'blob' });
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
        link.setAttribute('download', `disponibilita_${date}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('Report EXCEL scaricato');
      }
    } catch (error) {
      toast.error('Errore durante esportazione');
    }
  };

  const groupByDoccia = () => {
    const groups = {};
    filteredBins.forEach(bin => {
      const doccia = bin.doccia_number;
      if (!groups[doccia]) {
        groups[doccia] = [];
      }
      groups[doccia].push(bin);
    });
    return groups;
  };

  const groupByLotto = () => {
    const groups = {};
    filteredBins.forEach(bin => {
      const lotto = bin.lotto_number || 'N/D';
      if (!groups[lotto]) {
        groups[lotto] = {
          bins: [],
          fornitore: bin.fornitore,
          totaleKg: 0
        };
      }
      groups[lotto].bins.push(bin);
      groups[lotto].totaleKg += bin.kg_attuali || 0;
    });
    return groups;
  };

  const docciaGroups = groupByDoccia();
  const totalKg = filteredBins.reduce((sum, b) => sum + b.kg_attuali, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
        <div className="w-8 h-8 border-4 border-[#FFE500] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="disponibilita-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#FFE500] p-2 rounded-xl">
            <Package className="w-5 h-5 text-[#0A1628]" />
          </div>
          <h2 className="text-2xl font-bold text-white uppercase tracking-tight">Disponibilita Giacenze</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('pdf')}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-red-600 transition-colors"
            data-testid="export-pdf-btn"
          >
            <FileDown className="w-4 h-4" />
            PDF
          </button>
          <button
            onClick={() => handleExport('excel')}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-emerald-600 transition-colors"
            data-testid="export-excel-btn"
          >
            <FileDown className="w-4 h-4" />
            EXCEL
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="filters-section">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-[#FFE500]" />
          <h3 className="text-sm font-bold text-white uppercase tracking-tight">Filtri</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Data Entrata</label>
            <input
              type="date"
              value={filterData}
              onChange={(e) => setFilterData(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
              data-testid="data-filter"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Doccia/Fila</label>
            <select
              value={docciaFilter}
              onChange={(e) => setDocciaFilter(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
              data-testid="doccia-filter"
            >
              <option value="all" className="bg-[#0A1628]">Tutte</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                <option key={num} value={num} className="bg-[#0A1628]">Doccia {num}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Ricerca</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca bin, lotto o fornitore..."
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
              data-testid="search-input"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex gap-4 items-center">
            <span className="text-sm text-white/50">
              <span className="text-white font-medium">{filteredBins.length}</span> bins con giacenza
            </span>
            <span className="text-sm font-bold text-[#FFE500]">
              Totale: {totalKg.toFixed(2)} kg
            </span>
          </div>
          <div className="flex items-center gap-4">
            {/* Checkbox Raggruppa per Lotto */}
            <label className="flex items-center gap-2 cursor-pointer" data-testid="raggruppa-lotto-checkbox">
              <input
                type="checkbox"
                checked={raggruppaPerLotto}
                onChange={(e) => setRaggruppaPerLotto(e.target.checked)}
                className="w-4 h-4 rounded border-white/30 bg-white/10 text-[#FFE500] focus:ring-[#FFE500] focus:ring-offset-0"
              />
              <Layers className="w-4 h-4 text-[#FFE500]" />
              <span className="text-sm text-white font-medium">Raggruppa per Lotto</span>
            </label>
            {(filterData || docciaFilter !== 'all' || searchTerm) && (
              <button
                onClick={() => { setFilterData(''); setDocciaFilter('all'); setSearchTerm(''); }}
                className="text-sm text-red-400 hover:text-red-300 transition-colors"
                data-testid="clear-filters-btn"
              >
                Pulisci filtri
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl overflow-hidden" data-testid="disponibilita-table-container">
        <div className="overflow-x-auto">
          {raggruppaPerLotto ? (
            /* Vista raggruppata per LOTTO */
            <table className="text-sm w-full border-collapse" data-testid="disponibilita-table-lotto">
              <thead>
                <tr className="bg-[#FFE500]/10">
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">Lotto</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">ID</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">Fornitore</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">Bins</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">Doccia</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">Data Entrata</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">KG</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center bg-[#FFE500]/20">Totale Lotto</th>
                </tr>
              </thead>
              <tbody>
                {filteredBins.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-white/50" data-testid="no-results-message">
                      Nessuna giacenza disponibile.
                    </td>
                  </tr>
                ) : (
                  Object.entries(groupByLotto()).sort((a, b) => b[1].totaleKg - a[1].totaleKg).map(([lotto, lottoData]) => (
                    lottoData.bins.map((bin, idx) => (
                      <tr 
                        key={`${lotto}-${idx}`} 
                        className={`border-b border-white/10 hover:bg-white/5 transition-colors ${idx === lottoData.bins.length - 1 ? 'border-b-2 border-[#FFE500]/30' : ''}`}
                        data-testid={`disponibilita-lotto-row-${lotto}-${idx}`}
                      >
                        <td className="py-3 px-4 text-center font-bold text-lg text-[#FFE500]">
                          {idx === 0 ? lotto : ''}
                        </td>
                        <td className="py-3 px-4 text-center font-bold text-cyan-400">
                          {idx === 0 ? (lottoData.id_fornitore || bin.id_fornitore || '-') : ''}
                        </td>
                        <td className="py-3 px-4 text-center text-white/70">
                          {idx === 0 ? lottoData.fornitore : ''}
                        </td>
                        <td className="py-3 px-4 text-center font-medium text-white">{bin.bin_number}</td>
                        <td className="py-3 px-4 text-center text-white/70">{bin.doccia_number}</td>
                        <td className="py-3 px-4 text-center text-white/70">{format(new Date(bin.data_inizio), 'dd/MM/yyyy HH:mm')}</td>
                        <td className="py-3 px-4 text-center font-medium text-white">{bin.kg_attuali} kg</td>
                        <td className="py-3 px-4 text-center font-bold text-emerald-400 text-lg bg-emerald-400/10">
                          {idx === 0 ? `${lottoData.totaleKg.toFixed(2)} kg` : ''}
                        </td>
                      </tr>
                    ))
                  ))
                )}
              </tbody>
            </table>
          ) : (
            /* Vista standard per DOCCIA */
            <table className="text-sm w-full border-collapse" data-testid="disponibilita-table">
              <thead>
                <tr className="bg-[#FFE500]/10">
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">Doccia</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">Bins</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">Lotto</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">ID</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">Fornitore</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">Data Entrata</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">Orario</th>
                  <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-4 text-center">KG Disponibili</th>
                </tr>
              </thead>
              <tbody>
                {filteredBins.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-white/50" data-testid="no-results-message">
                      Nessuna giacenza disponibile.
                    </td>
                  </tr>
                ) : (
                  Object.keys(docciaGroups).sort((a, b) => parseInt(a) - parseInt(b)).map(doccia => (
                    docciaGroups[doccia].map((bin, idx) => (
                      <tr 
                        key={`${doccia}-${idx}`} 
                        className="border-b border-white/10 hover:bg-white/5 transition-colors"
                        data-testid={`disponibilita-row-${doccia}-${idx}`}
                      >
                        <td className="py-3 px-4 text-center font-bold text-xl text-white">
                          {idx === 0 ? doccia : ''}
                        </td>
                        <td className="py-3 px-4 text-center font-medium text-white">{bin.bin_number}</td>
                        <td className="py-3 px-4 text-center text-white/70">{bin.lotto_number}</td>
                        <td className="py-3 px-4 text-center font-bold text-cyan-400">{bin.id_fornitore || '-'}</td>
                        <td className="py-3 px-4 text-center text-white/70">{bin.fornitore}</td>
                        <td className="py-3 px-4 text-center text-white/70">{format(new Date(bin.data_inizio), 'dd/MM/yyyy')}</td>
                        <td className="py-3 px-4 text-center text-white/70">{format(new Date(bin.data_inizio), 'HH:mm')}</td>
                        <td className="py-3 px-4 text-center font-bold text-emerald-400 text-lg">{bin.kg_attuali} kg</td>
                      </tr>
                    ))
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Storico;
